import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import type { NextApiRequest } from 'next';

import {
  CustomContext,
  initializeApollo,
  initializeSecureApolloClient,
  saleorAuthClient,
} from '@/graphql/client';
import type {
  CustomerCreateMutationResult,
  LanguageCodeEnum,
  ProductVariantStrapDetailsQuery,
  ProductVariantStrapDetailsQueryVariables,
  ProductVariantWatchBasicDetailsQuery,
  ProductVariantWatchBasicDetailsQueryVariables,
  ProductVariantWatchDetailsQuery,
  ProductVariantWatchDetailsQueryVariables,
  UserCheckQueryHookResult,
  UserCreateInput,
  WatchBasicDetailsForSkusQuery,
  WatchBasicDetailsForSkusQueryVariables,
  WatchsBySkusQuery,
  WatchsBySkusQueryVariables,
} from '@/graphql/saleor/api';
import {
  ProductVariantStrapDetailsDocument,
  ProductVariantWatchBasicDetailsDocument,
  ProductVariantWatchDetailsDocument,
  WatchBasicDetailsForSkusDocument,
} from '@/graphql/saleor/api';
import { fetchStorefrontBffWithAuth } from '@/lib/auth';
import {
  ECS_ACCESS_PASSWORD,
  ECS_ACCESS_USERNAME,
  ECS_API_URI,
  IS_BROWSER,
} from '@/lib/const';
import { paths } from '@/lib/paths';

import {
  DETAIL_PAGE_IMAGE_SIZE,
  DETAIL_PAGE_LARGE_IMAGE_SIZE,
  NEXT_PUBLIC_IMAGES_FORMAT,
} from '../const';
import type { CPIResponse, MailerAppResponse, Warranty } from '../types';
import type {
  ArchivesSendFormParams,
  createRepairPaymentParams,
  createRepairPaymentResponse,
  CreateServiceRequestParams,
  PaymentMethodParams,
  RecommendPriceParams,
  RecommendPriceResponse,
  RefuseQuoteParams,
  RepairReason,
  RepairReasonParams,
  ResponseWithPage,
  ServiceDetails,
  trackingNumberForgottenParams,
  trackingNumberForgottenResponse,
  UpdateServiceRequestParams,
  WatchRegisterParams,
  WatchRegisterResponse,
} from './type';

export const buildQueryString = (query?: NextApiRequest['query']): string => {
  if (!query || Object.keys(query).length === 0) {
    return '';
  }

  const queryString = Object.entries(query)
    .filter(([key, value]) => key && value !== null && value !== undefined)
    .filter(([key, value]) => key && value)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return value
          .map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`)
          .join('&');
      } else {
        return `${encodeURIComponent(key)}=${
          value && encodeURIComponent(value)
        }`;
      }
    })
    .join('&');

  return `?${queryString}`;
};

type CustomRequestInit = RequestInit & {
  query?: NextApiRequest['query'];
};
export const fetchWithBasicAuth = (api: string, init?: CustomRequestInit) => {
  const requestInit = init ? omit(init, 'query') : {};
  const base64Credentials = Buffer.from(
    `${ECS_ACCESS_USERNAME}:${ECS_ACCESS_PASSWORD}`,
    'utf-8',
  ).toString('base64');
  const basicAuth = `Basic ${base64Credentials}`;
  return fetch(`${ECS_API_URI}${api}${buildQueryString(init?.query)}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: basicAuth,
    },
    ...requestInit,
  });
};

export const getRepairReason = async (
  params: RepairReasonParams,
): Promise<RepairReason | null> => {
  const response = await fetch(
    paths.api.ecs.repairReason.asPath({ query: params }),
  );
  if (!response.ok) {
    return null;
  }

  return await response.json();
};

export const getWarranty = async (
  sn: string,
  auth = true,
): Promise<CPIResponse<Warranty>> => {
  const api = auth ? saleorAuthClient.fetchWithAuth : fetch;
  const response = await api(
    paths.api.myWarranty + `?sn=${sn}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      cache: 'no-store',
    },
    { allowPassingTokenToThirdPartyDomains: true },
  );
  return await response.json();
};
export const getWatchDetailBySkuForSaleor = async (
  sku: string,
  channel: string,
  languageCode: LanguageCodeEnum,
) => {
  const client = initializeApollo();
  return client.query<
    ProductVariantWatchBasicDetailsQuery,
    ProductVariantWatchBasicDetailsQueryVariables
  >({
    query: ProductVariantWatchBasicDetailsDocument,
    variables: {
      sku,
      channel,
      languageCode,
      thumbnailSize: DETAIL_PAGE_IMAGE_SIZE,
      imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
      largeMediaSize: DETAIL_PAGE_LARGE_IMAGE_SIZE,
    },
  });
};

// It can obtain information about unpublished products, not applicable to all scenarios, use with caution
export const getWatchDetailsSecurely = async (
  sku: string,
  languageCode: LanguageCodeEnum,
): Promise<CPIResponse<ProductVariantWatchBasicDetailsQuery> | null> => {
  try {
    if (IS_BROWSER) {
      const url = paths.api.ecs.watchDetails.asPath({
        query: {
          sku,
          languageCode,
        },
      });
      const response = await fetch(url);
      if (!response.ok) {
        return null;
      }
      return await response.json();
    } else {
      const secureClient = initializeSecureApolloClient();
      const { data } = await secureClient.query<
        ProductVariantWatchBasicDetailsQuery,
        ProductVariantWatchBasicDetailsQueryVariables
      >({
        query: ProductVariantWatchBasicDetailsDocument,
        variables: {
          sku,
          languageCode,
          thumbnailSize: DETAIL_PAGE_IMAGE_SIZE,
          imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
          largeMediaSize: DETAIL_PAGE_LARGE_IMAGE_SIZE,
        },
      });

      return {
        code: 200,
        msg: '',
        data,
      };
    }
  } catch (error) {
    return null;
  }
};

export const getProductVariantWatchDetailBySkuForSaleor = async (
  sku: string,
  channel: string,
  languageCode: LanguageCodeEnum,
  disableErrorNotification = false,
) => {
  const client = initializeApollo();
  return client.query<
    ProductVariantWatchDetailsQuery,
    ProductVariantWatchDetailsQueryVariables
  >({
    query: ProductVariantWatchDetailsDocument,
    variables: {
      sku,
      channel,
      languageCode,
    },
    context: CustomContext({
      disableErrorNotification,
    }),
  });
};

export const getProductVariantStrapDetailBySkuForSaleor = async (
  sku: string,
  channel: string,
  languageCode: LanguageCodeEnum,
  disableErrorNotification = false,
) => {
  const client = initializeApollo();
  return client.query<
    ProductVariantStrapDetailsQuery,
    ProductVariantStrapDetailsQueryVariables
  >({
    query: ProductVariantStrapDetailsDocument,
    variables: {
      sku,
      channel,
      languageCode,
    },
    context: CustomContext({
      disableErrorNotification,
    }),
  });
};

export const createServiceRequest = async ({
  countryCode,
  ...params
}: CreateServiceRequestParams) => {
  const response = await fetch(
    paths.api.ecs.createRepair.asPath({ query: { countryCode } }),
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
  );
  if (!response.ok) {
    return null;
  }

  return await response.json();
};

export const getRepairDetail = async (
  query: NextApiRequest['query'],
  isAuth = false,
): Promise<CPIResponse<ResponseWithPage<ServiceDetails>> | null> => {
  // authorization-bearer keeps TST/PRE Cloudflare Basic Auth intact (do not use fetchWithAuth here)
  const response = isAuth
    ? await fetchStorefrontBffWithAuth(
        paths.api.ecs.repair.asPath() + buildQueryString(query),
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
    : await fetch(
        paths.api.ecs.repair.guest.asPath() + buildQueryString(query),
      );

  if (!response.ok) {
    return null;
  }

  return await response.json();
};

export const getRepairDetailBySSR = async (
  query: NextApiRequest['query'],
): Promise<ResponseWithPage<ServiceDetails> | null> => {
  const response = await fetchWithBasicAuth('GetRepairDetails', {
    query,
  });
  if (!response.ok) return null;
  return await response.json();
};

export const getRecommendPrice = async (
  params: RecommendPriceParams,
): Promise<RecommendPriceResponse | null> => {
  const response = await fetch(
    paths.api.ecs.recommendPrice.asPath({ query: params }),
    {
      method: 'GET',
    },
  );
  if (!response.ok) {
    return null;
  }

  return await response.json();
};

export const refuseQuote = async ({
  countryCode,
  ...params
}: RefuseQuoteParams) => {
  const response = await fetch(
    paths.api.ecs.refuseQuote.asPath({ query: { countryCode } }),
    {
      method: 'POST',
      body: JSON.stringify(params),
    },
  );
  if (!response.ok) {
    return null;
  }

  return await response.json();
};

export const getPaymentMethods = async (
  params: PaymentMethodParams,
): Promise<CPIResponse> => {
  const response = await fetch(paths.api.fast.getPaymentMethods.asPath(), {
    method: 'POST',
    body: JSON.stringify(params),
  });
  return await response.json();
};

export const createRepairPayment = async (
  params: createRepairPaymentParams,
): Promise<CPIResponse<createRepairPaymentResponse>> => {
  const response = await fetch(paths.api.fast.paymentSession.asPath(), {
    method: 'POST',
    body: JSON.stringify(params),
  });
  return await response.json();
};

export const updateServiceRepair = async ({
  countryCode,
  ...params
}: UpdateServiceRequestParams) => {
  const response = await fetch(
    paths.api.ecs.updateRepair.asPath({ query: { countryCode } }),
    {
      method: 'PUT',
      body: JSON.stringify(params),
    },
  );
  if (!response.ok) {
    return null;
  }
  return await response.json();
};

export const getWatchDetailsBySkus = async (
  skus: string[],
  channel: string,
) => {
  const client = initializeApollo();
  return client.query<
    WatchBasicDetailsForSkusQuery,
    WatchBasicDetailsForSkusQueryVariables
  >({
    query: WatchBasicDetailsForSkusDocument,
    variables: {
      skus,
      channel,
      thumbnailSize: DETAIL_PAGE_IMAGE_SIZE,
      imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
    },
  });
};

export const trackingNumberForgotten = async (
  params: trackingNumberForgottenParams,
): Promise<trackingNumberForgottenResponse | null> => {
  const response = await fetch(
    paths.api.ecs.tracking.asPath({ query: params }),
    {
      method: 'GET',
    },
  );
  if (!response.ok) {
    return null;
  }

  return await response.json();
};

const checkEmailPrivate = async (
  email: string,
): Promise<UserCheckQueryHookResult['data'] | null> => {
  const response = await fetch(paths.api.user.check.asPath(), {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
  if (!response.ok) {
    return null;
  }

  return await response.json();
};

export const createDebouncedFunction = <
  T extends (...args: any[]) => Promise<any>,
>(
  fn: T,
  wait: number,
): ((...args: Parameters<T>) => Promise<ReturnType<T> | undefined>) => {
  let pendingPromise: Promise<ReturnType<T>> | undefined;

  const debouncedFn = debounce(
    (resolve: (value: ReturnType<T>) => void, ...args: Parameters<T>) => {
      fn(...args).then(resolve);
    },
    wait,
  );

  return (...args: Parameters<T>): Promise<ReturnType<T>> => {
    if (!pendingPromise) {
      pendingPromise = new Promise<ReturnType<T>>((resolve) => {
        debouncedFn(resolve, ...args);
      }).finally(() => {
        pendingPromise = undefined;
      });
    }
    return pendingPromise;
  };
};

export const checkEmail = createDebouncedFunction(checkEmailPrivate, 800);

export const watchRegister = async (
  params: WatchRegisterParams,
): Promise<WatchRegisterResponse | null> => {
  const response = await fetch(paths.api.watch.watchRegister.asPath(), {
    method: 'POST',
    body: JSON.stringify(params),
  });

  return await response.json();
};

export const customerCreate = async (
  params: UserCreateInput,
): Promise<CustomerCreateMutationResult['data'] | null> => {
  const response = await fetch(paths.api.user.create.asPath(), {
    method: 'POST',
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    return null;
  }

  return await response.json();
};

export const archivesSendEmail = async (
  params: ArchivesSendFormParams,
): Promise<MailerAppResponse | null> => {
  const response = await fetch(paths.api.archivesSendEmail.asPath(), {
    method: 'POST',
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    throw { code: response.status, message: response.statusText };
  }

  return await response.json();
};

export const getWatchDetailWithoutChannel = async (
  params: WatchsBySkusQueryVariables,
): Promise<CPIResponse<WatchsBySkusQuery> | null> => {
  const response = await fetch(paths.api.getWatchesWithoutChannel.asPath(), {
    method: 'POST',
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    return null;
  }
  return await response.json();
};
